/**
 * Service Form Component
 * - Create new or edit existing veterinary services
 * - Handles form validation and image uploads
 * - Integrates with Redux for state management
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { FiEdit3 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { fetchServiceById, updateService, createService } from "../../redux/slices/servicesSlice";
import { toast } from "react-toastify";

export default function ServiceForm() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEditing = id && id !== "new";

  const { detail: service, detailStatus } = useSelector((state) => state.services);

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    description: "",
    active: true,
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  useEffect(() => {
    if (isEditing) {
      dispatch(fetchServiceById(id));
    }
  }, [dispatch, id, isEditing]);

  useEffect(() => {
    if (service && isEditing) {
      setFormData({
        name: service.name || "",
        title: service.title || "",
        description: service.description || "",
        active: service.active || false,
      });
      if (service.product_image_url) {
        setImagePreview(service.product_image_url);
      }
    }
  }, [service, isEditing]);

  if (detailStatus === "loading") return <div className="px-20 pt-10">Loading...</div>;
  if (detailStatus === "failed") return <div className="px-20 pt-10">Failed to load service data.</div>;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.name.trim() || !formData.title.trim() || !formData.description.trim()) {
        toast.error("Please fill in all required fields");
        return;
      }

      let dataToSend;

      if (selectedImage) {
        // Create FormData for image upload
        const formDataToSend = new FormData();
        formDataToSend.append('product[name]', formData.name);
        formDataToSend.append('product[title]', formData.title);
        formDataToSend.append('product[description]', formData.description);
        formDataToSend.append('product[active]', formData.active ? '1' : '0');
        formDataToSend.append('product[product_image_url]', selectedImage);
        dataToSend = formDataToSend;
      } else {
        // Send as JSON
        dataToSend = {
          name: formData.name,
          title: formData.title,
          description: formData.description,
          active: formData.active,
        };
      }

      if (isEditing) {
        await dispatch(updateService({ id, data: dataToSend })).unwrap();
        toast.success("Service updated successfully!");
      } else {
        await dispatch(createService(dataToSend)).unwrap();
        toast.success("Service created successfully!");
      }

      navigate("/swift-app/admin/services");
    } catch (error) {
      console.error("Save error:", error);
      const errorMessage = error?.response?.data?.meta?.message || `Failed to ${isEditing ? 'update' : 'create'} service. Please try again.`;
      toast.error(errorMessage);
    }
  };

  return (
    <div className="relative min-h-screen bg-white font-inter px-6 pt-12 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 md:gap-0 mb-10">
        <div className="flex items-start">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go Back"
            className="mt-1 mr-3 p-2 text-2xl text-gray-800 hover:text-blue-600"
          >
            <MdOutlineArrowBackIos />
          </button>
          <div>
            <div className="text-xl font-bold text-black">
              {isEditing ? `Edit Service: ${formData.name}` : "Add New Service"}
            </div>
            <div className="text-gray-600 text-sm mt-1">
              {isEditing ? "Update service information" : "Create a new veterinary service"}
            </div>
          </div>
        </div>

        {/* Desktop buttons - hidden on mobile */}
        <div className="hidden md:flex gap-3 md:self-end">
          <button
            onClick={() => navigate(-1)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#347DFF] text-white rounded-lg px-5 py-2 text-base font-medium hover:bg-blue-600 transition"
          >
            {isEditing ? "Save Changes" : "Create Service"}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-10 px-10">
        {/* Basic Service Information */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">
              <FiEdit3 size={22} />
            </span>{" "}
            Service Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">
                Service Name <span className="text-red-500">*</span>
              </label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Vaccination, Surgery, Check-up"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Service Title <span className="text-red-500">*</span>
              </label>
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Complete Health Examination"
                required
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">📝</span> Description
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Service Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="6"
              className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder="Describe the service in detail..."
              required
            />
          </div>
        </div>

        {/* Image Upload */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">📷</span> Service Image
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Upload Service Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {imagePreview && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-600">Preview:</div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Remove image
                  </button>
                </div>
                <img
                  src={imagePreview}
                  alt="Service preview"
                  className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                />
              </div>
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">⚙️</span> Status
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="text-sm font-medium text-gray-700">Active Service</label>
          </div>
        </div>

        {/* Mobile buttons - shown only on mobile after form */}
        <div className="md:hidden flex flex-col gap-3 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            className="w-full bg-[#347DFF] text-white rounded-lg px-5 py-3 text-base font-medium hover:bg-blue-600 transition"
          >
            {isEditing ? "Save Changes" : "Create Service"}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
